import BusinessActivity from '@/components/admin/BusinessActivity';
import BioDataAssistantHost from "@/components/assistant/BioDataAssistantHost";
import { requestAccountAction } from "@/components/assistant/account-actions.mjs";
import "@/App.css";
import GlobalHeader from "@/components/GlobalHeader";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useState, useEffect, lazy, Suspense, useCallback, Component } from "react";
import { Toaster } from "@/components/ui/sonner";
import { CompareProvider } from "@/context/CompareContext";
import { useSessionToken } from "@/auth/session";
import { PricingProvider } from "@/context/PricingContext";
import CompareDock from "@/components/CompareDock";
import CompareModal from "@/components/CompareModal";
import CommandPalette, { useKeyboardShortcuts } from "@/components/CommandPalette";
import AuthenticatedRoute from "@/auth/ProtectedRoute";
import AskInterface from "@/components/AskInterface";
import AskOverlayDialog from "@/components/ask/AskOverlayDialog";
import DemoModePanel from "@/components/DemoModePanel";
import ProfessionalResearchNotice from "@/components/ProfessionalResearchNotice";
import { LandingTrialHandoff } from '@/components/landing/LandingTrialHandoff';
import { MessageSquare } from "lucide-react";

// Root ErrorBoundary — prevents whitescreen on render exceptions
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("App ErrorBoundary:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", fontFamily: "system-ui" }}>
          <div style={{ textAlign: "center", maxWidth: 400, padding: 32 }}>
            <h1 style={{ fontSize: 20, fontWeight: 600, color: "#0f172a", marginBottom: 8 }}>Something went wrong</h1>
            <p style={{ fontSize: 14, color: "#64748b", marginBottom: 24 }}>An unexpected error occurred. Please refresh the page.</p>
            <button onClick={() => window.location.reload()} style={{ padding: "10px 24px", background: "#0f172a", color: "white", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lazy load pages for better initial load performance
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const HomePage = lazy(() => import("@/pages/HomePage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const MarketsPage = lazy(() => import("@/pages/MarketsPage"));
const WirePage = lazy(() => import("@/pages/WirePage"));
const ResearchPage = lazy(() => import("@/pages/ResearchPage"));
// AccessRadarPage removed in Phase 98 - pivoting to Market Intel
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const JoinPage = lazy(() => import("@/pages/JoinPage"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const MagicLinkCallback = lazy(() => import("@/pages/MagicLinkCallback"));
const BrandPage = lazy(() => import("@/pages/BrandPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const MessagesPage = lazy(() => import("@/components/communications/MessagesPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const MyExportsPage = lazy(() => import("@/pages/MyExportsPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const DrugIndexPage = lazy(() => import("@/pages/DrugIndexPage"));
const CompanyDossierPage = lazy(() => import("@/pages/CompanyDossierPage"));
const AssetReportPage = lazy(() => import("@/pages/AssetReportPage"));
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const OrangeBookImportPage = lazy(() => import("@/pages/OrangeBookImportPage"));
const WatchlistPage = lazy(() => import("@/pages/WatchlistPage"));
const PrivateMarketRadarPage = lazy(() => import("@/pages/PrivateMarketRadarPage"));
const PlanComparisonPage = lazy(() => import("@/pages/PlanComparisonPage"));
const OrgPortalPage = lazy(() => import("@/pages/OrgPortalPage"));
const TeamAIRPacksPage = lazy(() => import("@/pages/TeamAIRPacksPage"));
const CatalystsPage = lazy(() => import("@/pages/CatalystsPage"));
const AskPage = lazy(() => import("@/pages/AskPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const CompanyCoveragePage = lazy(() => import('@/pages/CompanyCoveragePage'));
const ResourceLibraryPage = lazy(() => import("@/pages/ResourceLibraryPage"));
const PublicResourcePage = lazy(() => import("@/pages/PublicResourcePage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-[#F9FBFF] flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-2 border-[#2A5D9E] border-t-transparent rounded-full animate-spin" />
      <span className="text-sm text-slate-500">Loading...</span>
    </div>
  </div>
);

// Redirect that preserves the :slug param (legacy /battlefield/:slug and /arena/battlefield/:slug)
function BattlefieldSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/markets?q=${encodeURIComponent(slug.replace(/-/g, " "))}`} replace />;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) throw new Error("REACT_APP_BACKEND_URL is required");
export const API = `${BACKEND_URL.replace(/\/$/, "")}/api`;

const ASK_WIDGET_ROUTE_PREFIXES = [
  "/overview", "/search", "/company-coverage", "/research", "/markets",
  "/dealflow", "/catalysts", "/admin", "/support", "/company", "/asset",
  "/exports", "/upgrade", "/watchlist", "/settings", "/payment/success",
  "/organization"
];

export const isAskWidgetRoute = (pathname) => ASK_WIDGET_ROUTE_PREFIXES.some(
  (route) => pathname === route || pathname.startsWith(`${route}/`)
) && !(pathname === "/admin" || pathname.startsWith("/admin/"));

// Export kept for existing consumers; the independently testable guard owns
// account-scoped authentication, expiry and recovery navigation.
export const ProtectedRoute = props => <AuthenticatedRoute {...props} api={API} />;

// Kept as a layout-compatible wrapper; the outer authenticated guard owns the
// single status check and the backend remains the report entitlement authority.
const SubscriptionGate = ({ children, user }) => typeof children === 'function' ? children({ user }) : children;

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions - session_id must be processed FIRST
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <LandingTrialHandoff />
      <BusinessActivity />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/messages" element={<ProtectedRoute allowSupport>{({user}) => <><GlobalHeader user={user}/><MessagesPage user={user}/></>}</ProtectedRoute>} />
        {/* Phase 120: Overview is the command center (home base after login) */}
        <Route
          path="/overview"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <DashboardPage user={user} setUser={setUser} />}
            </ProtectedRoute>
          }
        />
        {/* Ask — AI natural language front door */}
        <Route
          path="/ask"
          element={
            <ProtectedRoute>
              {({ user }) => <AskPage user={user} />}
            </ProtectedRoute>
          }
        />
        <Route path="/search" element={<ProtectedRoute>{({user}) => <SearchPage user={user}/>}</ProtectedRoute>} />
        <Route path="/company-coverage" element={<ProtectedRoute>{({user}) => <CompanyCoveragePage user={user}/>}</ProtectedRoute>} />
        {/* Legacy redirects to new routes */}
        <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
        <Route path="/home" element={<Navigate to="/overview" replace />} />
        
        {/* Phase 120: Research - Entity intelligence (Companies, Assets) */}
        <Route
          path="/research"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <SubscriptionGate user={user}><ResearchPage user={user} setUser={setUser} /></SubscriptionGate>}
            </ProtectedRoute>
          }
        />
        {/* Legacy redirect for scout */}
        <Route path="/scout" element={<Navigate to="/research" replace />} />
        <Route path="/scout/results" element={<Navigate to="/research" replace />} />
        
        {/* Phase 120: Markets - Market analysis (formerly Landscape) */}
        <Route
          path="/markets/:marketId?"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <SubscriptionGate user={user}><MarketsPage user={user} /></SubscriptionGate>}
            </ProtectedRoute>
          }
        />
        {/* Legacy redirects — preserve bookmarks */}
        <Route path="/landscape" element={<Navigate to="/markets" replace />} />
        <Route path="/strategy" element={<Navigate to="/markets" replace />} />
        <Route path="/arena" element={<Navigate to="/markets" replace />} />
        <Route path="/arena/battlefield/:slug" element={<BattlefieldSlugRedirect />} />
        
        {/* Phase 120: Monitor - News feed and watchlist */}
        <Route
          path="/monitor"
          element={
            <ProtectedRoute>
              {({ user, setUser }) => <WirePage user={user} setUser={setUser} />}
            </ProtectedRoute>
          }
        />
        {/* Legacy redirect for wire */}
        <Route path="/wire" element={<Navigate to="/monitor" replace />} />
      {/* Access Radar route removed in Phase 98 - replaced by Market Intel */}

      {/* Phase 135: Dealflow — Private Market Dealflow (formerly Radar) */}
      <Route
        path="/dealflow"
        element={
          <ProtectedRoute>
            {({ user }) => <SubscriptionGate user={user}><PrivateMarketRadarPage /></SubscriptionGate>}
          </ProtectedRoute>
        }
      />

      {/* Legacy redirects — preserve bookmarks */}
      <Route path="/private-markets" element={<Navigate to="/dealflow" replace />} />
      <Route path="/radar" element={<Navigate to="/dealflow" replace />} />

      {/* Redirect /airpacks to /exports (AIRPacks live in My Exports now) */}
      <Route path="/airpacks" element={<Navigate to="/exports" replace />} />

      {/* Catalysts Calendar */}
      <Route
        path="/catalysts"
        element={
          <ProtectedRoute>
            {({ user }) => <CatalystsPage user={user} />}
          </ProtectedRoute>
        }
      />

      {/* Strategic Battlefield Maps — now nested under /markets */}
      <Route
        path="/markets/battlefield/:slug"
        element={
          <BattlefieldSlugRedirect />
        }
      />
      {/* Legacy redirect for bookmarks */}
      <Route path="/battlefield/:slug" element={<BattlefieldSlugRedirect />} />

      {/* Arena route removed — merged into /markets. Redirect preserves bookmarks. */}

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <AdminPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      {/* Phase 95: Orange Book Import */}
      <Route
        path="/admin/orange-book"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <OrangeBookImportPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic/:token" element={<MagicLinkCallback />} />
      <Route
        path="/brand"
        element={<BrandPage />}
      />
      <Route
        path="/support"
        element={
          <ProtectedRoute allowSupport>
            {({ user }) => <SupportPage user={user}/>}
          </ProtectedRoute>
        }
      />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/resources" element={<ResourceLibraryPage />} />
      <Route path="/resources/:slug" element={<PublicResourcePage />} />
      {/* Knowledge Base - Phase 49 */}
      <Route path="/help/drug-index" element={<DrugIndexPage />} />
      {/* Phase 54: Company Dossier */}
      <Route
        path="/company/:ticker"
        element={
          <ProtectedRoute>
            {({ user }) => <SubscriptionGate user={user}><CompanyDossierPage user={user} /></SubscriptionGate>}
          </ProtectedRoute>
        }
      />
      {/* Phase 71: Asset Report (Drug/Molecule Intelligence) */}
      <Route
        path="/asset/:assetName"
        element={
          <ProtectedRoute>
            {({ user }) => <SubscriptionGate user={user}><AssetReportPage user={user} /></SubscriptionGate>}
          </ProtectedRoute>
        }
      />
      <Route
        path="/exports"
        element={
          <ProtectedRoute>
            {({ user }) => <SubscriptionGate user={user}><MyExportsPage user={user} /></SubscriptionGate>}
          </ProtectedRoute>
        }
      />
      {/* Redirect legacy credits page to upgrade */}
      <Route
        path="/credits"
        element={
          <ProtectedRoute allowSupport>{() => <Navigate to="/upgrade" replace />}</ProtectedRoute>
        }
      />
      <Route
        path="/upgrade"
        element={
          <ProtectedRoute allowSupport>
            {({ user, handleLogout }) => <UpgradePage user={user} onLogout={handleLogout} />}
          </ProtectedRoute>
        }
      />
      {/* Phase 113: Watchlist Management */}
      <Route
        path="/watchlist"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <WatchlistPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      {/* Phase 68: User Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {({ user }) => <SettingsPage user={user} />}
          </ProtectedRoute>
        }
      />
      {/* Phase 69: Team Management */}
      <Route
        path="/settings/team"
        element={
          <ProtectedRoute>
            {({ user }) => <SettingsPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute>
            {({ user }) => <PaymentSuccessPage user={user} />}
          </ProtectedRoute>
        }
      />
      {/* Public: Plan comparison (no auth) */}
      <Route path="/plans" element={<PlanComparisonPage />} />
      {/* Protected: Org self-service portal */}
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            {({ user }) => <OrgPortalPage user={user} />}
          </ProtectedRoute>
        }
      />
    </Routes>
    </Suspense>
  );
}

// App with keyboard shortcuts and command palette
function AppWithShortcuts() {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [askOverlayOpen, setAskOverlayOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [askUser,setAskUser]=useState(null);
  const [authStatus, setAuthStatus] = useState("loading");
  const location = useLocation();
  const isAskPage = location.pathname === "/ask";
  const askWidgetAvailable = authStatus === "authenticated" && !isAskPage && isAskWidgetRoute(location.pathname);
  
  // Recheck after SPA navigation, including sign-in and sign-out.
  useEffect(() => {
    let active = true;
    const checkAdmin = async () => {
      const token = localStorage.getItem("session_token");
      const clear = () => {if(active){setAuthStatus("unauthenticated");setAskUser(null);setIsAdmin(false);}};
      if (!token) {clear();return;}
      try {
        const res = await fetch(`${API}/auth/me`, {headers: { Authorization: `Bearer ${token}` }});
        if (!active || localStorage.getItem("session_token") !== token) return;
        if (!res.ok) {clear();return;}
        const user = await res.json();
        if (!active || localStorage.getItem("session_token") !== token) return;
        setAuthStatus("authenticated");setAskUser(user);
        setIsAdmin(user.role === "super_admin" || user.is_super_admin);
      } catch (e) {clear();}
    };
    checkAdmin();
    return () => {active = false;};
  }, [location.pathname]);

  // Cmd+K / Ctrl+K — open Ask overlay (skip if typing in an input/textarea)
  useEffect(() => {
    if (!askWidgetAvailable) {
      setAskOverlayOpen(false);
      return undefined;
    }
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      if (modifier && e.key === "k") {
        const active = document.activeElement;
        if (
          active &&
          (active.tagName === "INPUT" ||
            active.tagName === "TEXTAREA" ||
            active.isContentEditable)
        ) {
          return; // Don't intercept when user is typing
        }
        e.preventDefault();
        setAskOverlayOpen((prev) => !prev);
      }

      if (e.key === "Escape" && askOverlayOpen) {
        setAskOverlayOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [askOverlayOpen, askWidgetAvailable]);
  
  // Open help drawer
  const openHelp = useCallback(() => {
    const helpBtn = document.querySelector('[title="Help & Guide"]');
    helpBtn?.click();
  }, []);
  
  // Register remaining keyboard shortcuts (Cmd+/ for help, etc.)
  useKeyboardShortcuts({
    onOpenHelp: openHelp
  });
  
  return (
    <>
      <AppRouter />
      <ProfessionalResearchNotice />
      <CompareDock />
      <CompareModal />
      <CommandPalette 
        isOpen={commandPaletteOpen} 
        onClose={() => setCommandPaletteOpen(false)}
        isAdmin={isAdmin}
      />
      <DemoModePanel />

      {/* Floating Ask button — authenticated application routes only */}
      {askWidgetAvailable && (
        <button
          data-testid="ask-floating-btn"
          onClick={() => setAskOverlayOpen(true)}
          title="Ask AI (⌘K)"
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            width: "108px",
            height: "44px",
            borderRadius: "24px",
            background: "#161d2b",
            border: "1px solid #374151",
            color: "#9ca3af",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 49,
            transition: "background 0.15s, border-color 0.15s, color 0.15s",
            boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#1f2937";
            e.currentTarget.style.borderColor = "#4b5563";
            e.currentTarget.style.color = "#e5e7eb";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#161d2b";
            e.currentTarget.style.borderColor = "#374151";
            e.currentTarget.style.color = "#9ca3af";
          }}
        >
          <MessageSquare size={16} /><span style={{marginLeft:8,color:"#fff"}}>Ask AI</span>
        </button>
      )}

      {/* Ask Overlay — Cmd+K modal */}
      {askWidgetAvailable && askOverlayOpen && (
        <AskOverlayDialog onClose={() => setAskOverlayOpen(false)}>
          <AskInterface scopeKey={`${askUser?.user_id || askUser?.id || ''}:${askUser?.org_id || ''}`} mode="overlay" onClose={() => setAskOverlayOpen(false)} />
        </AskOverlayDialog>
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <div className="App">
      <BrowserRouter>
        <BioDataAssistantHost enabled={process.env.REACT_APP_ASSISTANT_ENABLED === "true"} openAccount={requestAccountAction} />
        <PricingProvider>
          <CompareProvider>
              <AppWithShortcuts />
          </CompareProvider>
        </PricingProvider>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
    </ErrorBoundary>
  );
}

export default App;
